import { useEffect, useState } from "react";
import { legalEntityService } from "../../Api/Services/legalEntityService";
import { accountService } from "../../Api/Services/accountService";
import { LegalEntity } from "../../Models/LegalEntity";
import { formatCnpj, formatPhoneNumber, unformat } from "../../Utils/DataFormatters";
import { Input } from "../Input/Input";
import { toast } from "react-toastify";
import Button from "../Button/Button";

type LegalEntityFormProps = {
  legalEntity: LegalEntity;
  email: string;
  phoneNumber: string;
  onSave: () => void;
};

const LegalEntityForm = ({ legalEntity, email, phoneNumber, onSave }: LegalEntityFormProps) => {
  const [editedCompanyName, setEditedCompanyName] = useState<string | null>(null);
  const [editedTradeName, setEditedTradeName] = useState<string | null>(null);
  const [editedPhoneNumber, setEditedPhoneNumber] = useState<string | null>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    const hasChanges = editedCompanyName !== null || editedTradeName !== null || editedPhoneNumber !== null;
    setIsButtonEnabled(hasChanges);
  }, [editedCompanyName, editedTradeName, editedPhoneNumber]);

  const handleSubmit = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    let updatedLegalEntity: boolean = false;
    let updatedPhoneNumber: boolean = false;

    try {
      if (editedCompanyName !== null || editedTradeName !== null) {
        updatedLegalEntity = await legalEntityService.update({
          id: legalEntity.id,
          companyName: editedCompanyName, 
          tradeName: editedTradeName 
        });
      }

      if (editedPhoneNumber !== null) {
        try {
          const updated = await accountService.updatePhoneNumber({ 
            phoneNumber: editedPhoneNumber 
          });
          if (!updated) {
            toast.error("Erro ao atualizar telefone.");
            return;
          }
          updatedPhoneNumber = true;
          const formattedPhone = formatPhoneNumber(editedPhoneNumber);
          setEditedPhoneNumber(formattedPhone);
        } catch (err: any) {
          const message = err?.response?.data as string | undefined;
          toast.error(message ?? "Erro ao atualizar telefone.");
          return;
        }
      }

      if (updatedLegalEntity || updatedPhoneNumber) {
        onSave();
        toast.success("Alterações salvas com sucesso!");

        setEditedCompanyName(null);
        setEditedTradeName(null);
        setEditedPhoneNumber(null);
        setIsButtonEnabled(false);
      }
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="flex flex-col gap-3">
      <form className="space-y-4">
        <Input
          label={"Razão Social"}
          type="text"
          name="companyName"
          value={editedCompanyName ?? legalEntity.companyName}
          onChange={e => setEditedCompanyName(e.target.value)}
          disabled={isSaving}
          required
        />

        <Input
          label={"Nome Fantasia"}
          type="text"
          name="tradeName"
          value={editedTradeName ?? legalEntity.tradeName ?? ""}
          onChange={e => setEditedTradeName(e.target.value)}
          disabled={isSaving}
        />  

        <Input
          label={"Telefone celular"}
          type="tel"
          name="phoneNumber"
          value={formatPhoneNumber(editedPhoneNumber ?? phoneNumber)}
          onChange={e => setEditedPhoneNumber(unformat(e.target.value))}
          disabled={isSaving}
          required
        />

        <Input
          label={"E-mail"}
          type="email"
          name="email"
          value={email}
          readOnly
        />

        <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-2">
          <div className="sm:flex-1">
            <Input
              label={"CNPJ"}
              type="text"
              name="cnpj"
              value={formatCnpj(legalEntity.cnpj)}
              readOnly
            />
          </div>
          <div className="sm:flex-1">
            <Input
              label={"Inscrição Estadual"}
              type="text"
              name="stateRegistration"
              value={
                legalEntity.isStateRegistrationExempt
                  ? "Isento"
                  : legalEntity.stateRegistration ?? ""
              }
              readOnly
            />
          </div>
        </div>
      </form>
      <p className="text-xs text-gray-500">{"(*) Campos obrigatórios."}</p>
      <Button isLoading={isSaving}
        disabled={!isButtonEnabled || isSaving}
        className="w-full py-2"
        onClick={handleSubmit}
      >
        {"Salvar alterações"}
      </Button>
    </div>           
  );
};

export default LegalEntityForm;
